import Swal from 'sweetalert2';
import { toast } from "react-toastify";

// Normalisasi berbagai bentuk payload error validasi agar aman ditampilkan ke toast.
const getValidationMessages = (errors) => {
	if (!errors || typeof errors !== 'object') {
		return [];
	}

	return Object.values(errors)
		.flatMap((fieldError) => {
			if (Array.isArray(fieldError)) {
				return fieldError;
			}

			if (typeof fieldError === 'string') {
				return [fieldError];
			}

			return [];
		})
		.filter(Boolean);
};

// Ambil pesan error paling relevan dari berbagai bentuk response backend.
const getApiResponseMessage = (error) => {
	const data = error?.response?.data;

	if (!data) {
		return '';
	}

	if (typeof data === 'string') {
		return data;
	}

	if (typeof data?.message === 'string' && data.message.trim() !== '') {
		return data.message;
	}

	if (typeof data?.error === 'string' && data.error.trim() !== '') {
		return data.error;
	}

	const validationMessages = getValidationMessages(data?.errors);
	if (validationMessages.length > 0) {
		return validationMessages[0];
	}

	return '';
};

// Buat fallback message agar tidak selalu tampil pesan generik.
const getFallbackByStatus = (statusCode, responseMessage) => {
	if (responseMessage) {
		return responseMessage;
	}

	if (statusCode >= 500) {
		return `Terjadi gangguan pada server (HTTP ${statusCode}). Mohon coba lagi beberapa saat.`;
	}

	if (statusCode >= 400) {
		return `Permintaan gagal diproses (HTTP ${statusCode}).`;
	}

	return 'Terjadi kesalahan. Mohon coba lagi nanti.';
};

// Logging ringkas untuk bantu tracing kasus admin di browser console.
const logErrorForDebugging = (error) => {
	const statusCode = error?.response?.status;
	const method = error?.config?.method?.toUpperCase?.() || '-';
	const url = error?.config?.url || '-';
	const requestId = error?.response?.headers?.['x-request-id'] || error?.response?.headers?.['x-correlation-id'] || '-';

	console.error('[API ERROR]', {
		statusCode,
		method,
		url,
		requestId,
		data: error?.response?.data,
	});
};

export const handleApiError = (error) => {
	let errorMessage = 'Terjadi kesalahan. Mohon coba lagi nanti.';
	let errorTitle = 'Error';
	const responseMessage = getApiResponseMessage(error);

	if (error?.response) {
		const statusCode = error.response.status;

		// Tentukan judul dan pesan error berdasarkan status code
		switch (statusCode) {
			case 400:
				errorMessage = responseMessage || 'Permintaan tidak valid.';
				errorTitle = 'Bad Request';
				break;
			case 401:
				errorMessage = responseMessage || 'Anda tidak diotorisasi untuk mengakses ini.';
				errorTitle = 'Unauthorized';
				break;
			case 403:
				errorMessage = responseMessage || 'Anda tidak memiliki izin untuk mengakses ini.';
				errorTitle = 'Forbidden';
				break;
			case 404:
				errorMessage = responseMessage || 'Sumber daya tidak ditemukan.';
				errorTitle = 'Not Found';
				break;
			case 422: // Handle error validasi
				const validationMessages = getValidationMessages(error.response?.data?.errors);

				if (validationMessages.length > 0) {
					[...new Set(validationMessages)].forEach((message) => {
						toast.error(message, {
							position: toast.POSITION.TOP_RIGHT,
							autoClose: 3000,
						});
					});

					throw error; // Tetap lempar error agar alur caller tetap konsisten.
				}

				errorMessage = responseMessage || 'Data yang dikirim tidak valid.';
				errorTitle = 'Validation Error';
				break;
			case 429:
				errorMessage = responseMessage || 'Terlalu banyak permintaan. Coba lagi 1 menit lagi.';
				errorTitle = 'Too Many Requests';
				break;
			case 500:
				errorMessage = responseMessage || 'Terjadi kesalahan internal server. Mohon hubungi pengembang.';
				errorTitle = 'Internal Server Error';
				break;
			default:
				errorMessage = getFallbackByStatus(statusCode, responseMessage);
				errorTitle = `${statusCode} Error`;
				break;
		}

		logErrorForDebugging(error);

		Swal.fire({
			icon: 'error',
			title: errorTitle,  // Menampilkan judul error spesifik
			text: errorMessage, // Menampilkan pesan error
		});
	} else if (error?.request) {
		Swal.fire({
			icon: 'error',
			title: 'Network Error',
			text: 'Permintaan gagal. Mohon periksa koneksi internet Anda.',
		});
	} else {
		Swal.fire({
			icon: 'error',
			title: 'Error',
			text: error?.message || 'Terjadi kesalahan. Mohon coba lagi nanti.',
		});
	}

	throw error;  // Lempar error untuk penanganan lebih lanjut jika diperlukan
};